import React, { useState } from 'react';
import {
  View,
  Text,
  Modal,
  TouchableOpacity,
  StyleSheet,
  ScrollView,
  Dimensions,
  Alert,
  Share,
  Linking,
  ActivityIndicator,
  Platform,
} from 'react-native';
import AsyncStorage from '@react-native-async-storage/async-storage';
import { Order } from '../types';
import APIService from '../services/api';
import * as Print from 'expo-print';
import * as Sharing from 'expo-sharing';

interface OrderSummaryModalProps {
  visible: boolean;
  order: Order | null;
  onClose: () => void;
  onPrint?: () => void;
}

const OrderSummaryModal: React.FC<OrderSummaryModalProps> = ({
  visible,
  order,
  onClose,
  onPrint
}) => {
  const [isSharingWhatsApp, setIsSharingWhatsApp] = useState(false);
  const [isGeneratingPDF, setIsGeneratingPDF] = useState(false);
  
  if (!order) return null;

  const currentDate = new Date().toLocaleDateString('es-GT', {
    weekday: 'long',
    year: 'numeric',
    month: 'long',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });

  const handlePrint = async () => {
    // Evitar ejecuciones múltiples
    if (isGeneratingPDF) {
      console.log('⚠️ PDF ya se está generando, ignorando clic duplicado');
      return;
    }
    
    try {
      setIsGeneratingPDF(true);
      
      // Crear HTML para el PDF
      const htmlContent = `
        <!DOCTYPE html>
        <html>
          <head>
            <meta charset="utf-8">
            <title>Pedido ${order.orderNumber}</title>
            <style>
              body {
                font-family: Arial, sans-serif;
                padding: 20px;
                color: #333;
              }
              .header {
                text-align: center;
                margin-bottom: 30px;
                border-bottom: 3px solid #e74c3c;
                padding-bottom: 15px;
              }
              .header h1 {
                color: #e74c3c;
                margin: 0;
                font-size: 24px;
              }
              .section {
                margin: 20px 0;
                padding: 15px;
                background-color: #f8f9fa;
                border-radius: 8px;
              }
              .section-title {
                font-size: 18px;
                font-weight: bold;
                color: #2c3e50;
                margin-bottom: 10px;
              }
              .info-row {
                display: flex;
                justify-content: space-between;
                margin: 8px 0;
                padding: 5px 0;
              }
              .label {
                font-weight: 600;
                color: #7f8c8d;
              }
              .value {
                color: #2c3e50;
              }
              .status {
                color: #27ae60;
                text-transform: uppercase;
                font-weight: bold;
              }
              .product-row {
                border-bottom: 1px solid #ecf0f1;
                padding: 10px 0;
                margin: 10px 0;
              }
              .product-name {
                font-weight: bold;
                font-size: 14px;
                color: #2c3e50;
              }
              .product-details {
                font-size: 12px;
                color: #7f8c8d;
                margin-top: 4px;
              }
              .total-row {
                display: flex;
                justify-content: space-between;
                padding: 8px 0;
                font-size: 14px;
              }
              .grand-total {
                border-top: 2px solid #e74c3c;
                padding-top: 12px;
                margin-top: 12px;
              }
              .grand-total .label {
                font-size: 18px;
                font-weight: bold;
                color: #e74c3c;
              }
              .grand-total .value {
                font-size: 20px;
                font-weight: bold;
                color: #e74c3c;
              }
              .footer {
                text-align: center;
                margin-top: 30px;
                padding-top: 20px;
                border-top: 2px solid #ecf0f1;
                color: #7f8c8d;
                font-size: 12px;
              }
            </style>
          </head>
          <body>
            <div class="header">
              <h1>🧾 RESUMEN DE PEDIDO</h1>
              <p style="margin: 5px 0;">MotoMarketGT</p>
            </div>

            <div class="section">
              <div class="section-title">📋 Información del Pedido</div>
              <div class="info-row">
                <span class="label">Número de Pedido:</span>
                <span class="value">${order.orderNumber}</span>
              </div>
              <div class="info-row">
                <span class="label">Fecha:</span>
                <span class="value">${currentDate}</span>
              </div>
              <div class="info-row">
                <span class="label">Estado:</span>
                <span class="value status">${order.status}</span>
              </div>
            </div>

            <div class="section">
              <div class="section-title">🛍️ Productos</div>
              ${order.items.map((item) => {
                const itemTotal = typeof item.total === 'number' ? item.total : parseFloat(item.total) || 0;
                const itemQuantity = item.quantity || 1;
                const unitPrice = itemTotal / itemQuantity;
                
                return `
                  <div class="product-row">
                    <div class="product-name">${item.product?.nombre || 'Producto'}</div>
                    <div class="product-details">
                      Cantidad: ${itemQuantity} × Q${unitPrice.toFixed(2)} = Q${itemTotal.toFixed(2)}
                    </div>
                  </div>
                `;
              }).join('')}
            </div>

            <div class="section">
              <div class="section-title">💰 Resumen de Costos</div>
              <div class="total-row">
                <span class="label">Subtotal:</span>
                <span class="value">Q${(typeof order.subtotal === 'number' ? order.subtotal : parseFloat(order.subtotal) || 0).toFixed(2)}</span>
              </div>
              <div class="total-row">
                <span class="label">Impuestos:</span>
                <span class="value">Q${(typeof order.taxes === 'number' ? order.taxes : parseFloat(order.taxes) || 0).toFixed(2)}</span>
              </div>
              <div class="total-row">
                <span class="label">Envío:</span>
                <span class="value">Q${(typeof order.shipping === 'number' ? order.shipping : parseFloat(order.shipping) || 0).toFixed(2)}</span>
              </div>
              ${(order as any).walletPayment > 0 ? `
              <div class="total-row" style="color: #27ae60; font-weight: 600;">
                <span class="label">💰 Billetera Virtual:</span>
                <span class="value">-Q${((order as any).walletPayment || 0).toFixed(2)}</span>
              </div>
              ${(order as any).complementaryPayment > 0 ? `
              <div class="total-row">
                <span class="label">Pago Complementario:</span>
                <span class="value">Q${((order as any).complementaryPayment || 0).toFixed(2)}</span>
              </div>` : ''}
              <div class="total-row" style="margin-top: 10px; color: #3498db; font-style: italic;">
                <span class="label">Saldo Restante:</span>
                <span class="value">Q${((order as any).newWalletBalance || 0).toFixed(2)}</span>
              </div>` : ''}
              <div class="total-row grand-total">
                <span class="label">TOTAL:</span>
                <span class="value">Q${(typeof order.total === 'number' ? order.total : parseFloat(order.total) || 0).toFixed(2)}</span>
              </div>
            </div>

            <div class="section">
              <div class="section-title">📍 Dirección de Envío</div>
              <p style="margin: 5px 0;">${order.shippingAddress?.name}</p>
              <p style="margin: 5px 0;">${order.shippingAddress?.address}</p>
              <p style="margin: 5px 0;">${order.shippingAddress?.city}</p>
              <p style="margin: 5px 0;">Tel: ${order.shippingAddress?.phone}</p>
            </div>

            <div class="section">
              <div class="section-title">💳 Método de Pago</div>
              <p style="margin: 5px 0; font-weight: 600;">${order.paymentMethod?.descripcion || 'N/A'}</p>
            </div>

            <div class="footer">
              <p>¡Gracias por tu compra!</p>
              <p>MotoMarketGT - Tu tienda de confianza</p>
              <p>www.motomarket.com.gt</p>
            </div>
          </body>
        </html>
      `;

      console.log('📄 Generando PDF...');
      
      // En web, usar diálogo de impresión del navegador
      if (Platform.OS === 'web') {
        console.log('🌐 Plataforma web detectada, usando diálogo de impresión');
        const iframe = document.createElement('iframe');
        iframe.style.position = 'absolute';
        iframe.style.width = '0';
        iframe.style.height = '0';
        iframe.style.border = 'none';
        
        document.body.appendChild(iframe);
        
        const doc = iframe.contentWindow?.document;
        if (doc) {
          doc.open();
          doc.write(htmlContent);
          doc.close();
          
          // Esperar a que se cargue el contenido
          setTimeout(() => {
            iframe.contentWindow?.print();
            // Eliminar el iframe después de imprimir
            setTimeout(() => document.body.removeChild(iframe), 1000);
          }, 250);
        }
        return; // Salir aquí para web
      }
      
      // Para móvil nativo: Generar PDF y compartir
      const result = await Print.printToFileAsync({
        html: htmlContent,
        base64: false
      });

      if (!result || !result.uri) {
        console.log('⚠️ No se pudo generar el PDF');
        Alert.alert(
          '⚠️ Error',
          'No se pudo generar el PDF en este dispositivo',
          [{ text: 'OK' }]
        );
        return;
      }

      const { uri } = result;
      console.log('✅ PDF generado:', uri);

      // Verificar si se puede compartir
      const canShare = await Sharing.isAvailableAsync();
      
      if (canShare) {
        // Compartir el PDF
        await Sharing.shareAsync(uri, {
          mimeType: 'application/pdf',
          dialogTitle: `Pedido ${order.orderNumber}`,
          UTI: 'com.adobe.pdf'
        });
        
        console.log('✅ PDF compartido exitosamente');
        
        Alert.alert(
          '✅ Éxito',
          'PDF generado y compartido correctamente',
          [{ text: 'OK' }]
        );
      } else {
        Alert.alert(
          '⚠️ Aviso',
          'PDF generado pero no se puede compartir en este dispositivo',
          [{ text: 'OK' }]
        );
      }

      // No llamar a onPrint para evitar doble generación
      // if (onPrint) onPrint();
    } catch (error: any) {
      console.error('❌ Error al generar PDF:', error);
      Alert.alert(
        '❌ Error',
        'No se pudo generar el PDF del pedido. Por favor, intenta nuevamente.',
        [{ text: 'OK' }]
      );
    } finally {
      setIsGeneratingPDF(false);
    }
  };

  const handleClose = async () => {
    try {
      setIsSharingWhatsApp(true);
      
      console.log('🔄 OrderSummaryModal: Iniciando proceso de cierre');
      
      // Obtener configuración de empresa
      const config = await APIService.getEmpresaConfig();
      console.log('📋 Configuración obtenida:', config);
      
      // 1. ENVIAR CORREOS DE CONFIRMACIÓN
      try {
        console.log('📧 Enviando correos de confirmación...');
        
        // Obtener datos del usuario desde AsyncStorage
        const userDataString = await AsyncStorage.getItem('userData');
        const userData = userDataString ? JSON.parse(userDataString) : null;
        const clienteEmail = userData?.email || '';
        const clienteNombre = userData?.nombre || userData?.name || 'Cliente';
        
        console.log('👤 Cliente:', clienteNombre, clienteEmail);
        
        if (clienteEmail) {
          // Preparar detalles del pedido para el correo
          const productosHtml = order.items.map((item) => {
            const itemTotal = typeof item.total === 'number' ? item.total : parseFloat(item.total) || 0;
            const itemQuantity = item.quantity || 1;
            const unitPrice = itemTotal / itemQuantity;
            
            return `
              <div style="padding: 10px 0; border-bottom: 1px solid #eee;">
                <strong>${item.product?.nombre || 'Producto'}</strong><br>
                Cantidad: ${itemQuantity} × Q${unitPrice.toFixed(2)} = <strong>Q${itemTotal.toFixed(2)}</strong>
              </div>
            `;
          }).join('');
          
          const pedidoDetalles = {
            cliente_nombre: clienteNombre,
            fecha: currentDate,
            productos_html: productosHtml,
            subtotal: (typeof order.subtotal === 'number' ? order.subtotal : parseFloat(order.subtotal) || 0).toFixed(2),
            impuestos: (typeof order.taxes === 'number' ? order.taxes : parseFloat(order.taxes) || 0).toFixed(2),
            envio: (typeof order.shipping === 'number' ? order.shipping : parseFloat(order.shipping) || 0).toFixed(2),
            total: (typeof order.total === 'number' ? order.total : parseFloat(order.total) || 0).toFixed(2),
            direccion: `${order.shippingAddress?.name || ''}<br>${order.shippingAddress?.address || ''}<br>${order.shippingAddress?.city || ''}<br>Tel: ${order.shippingAddress?.phone || ''}`,
          };
          
          // Enviar correos
          const emailResponse = await APIService.sendOrderConfirmationEmail(
            order.orderNumber,
            clienteEmail,
            pedidoDetalles
          );
          
          console.log('✅ Respuesta de envío de correos:', emailResponse);
          
          if (emailResponse.success) {
            console.log('📧 ✅ Correos enviados exitosamente');
          }
        } else {
          console.log('⚠️ No se encontró email del cliente, no se enviaron correos');
        }
      } catch (emailError) {
        console.error('❌ Error al enviar correos:', emailError);
        // Continuar aunque falle el envío de correos
      }
      
      console.log('✅ Preparando cierre de modal');
      // La llamada de WhatsApp se hace en el parent (CartCheckoutScreen)
      // No es necesario duplicarla aquí
      onClose();
      
    } catch (error: any) {
      console.error('❌ Error en handleClose:', error);
      console.error('📄 Error completo:', JSON.stringify(error, null, 2));
      // Si hay error, simplemente cerrar el modal
      onClose();
    } finally {
      setIsSharingWhatsApp(false);
    }
  };

  return (
    <Modal
      visible={visible}
      animationType="slide"
      presentationStyle="pageSheet"
      onRequestClose={onClose}
    >
      <View style={styles.container}>
        <View style={styles.header}>
          <Text style={styles.title}>🧾 Resumen del Pedido</Text>
          <TouchableOpacity onPress={onClose} style={styles.closeButton}>
            <Text style={styles.closeButtonText}>✕</Text>
          </TouchableOpacity>
        </View>

        <ScrollView style={styles.content} showsVerticalScrollIndicator={false}>
          {/* Información del pedido */}
          <View style={styles.section}>
            <Text style={styles.sectionTitle}>📋 Info Pedido</Text>
            <View style={styles.infoRow}>
              <Text style={styles.label}>Pedido #:</Text>
              <Text style={styles.value}>{order.orderNumber}</Text>
            </View>
            <View style={styles.infoRow}>
              <Text style={styles.label}>Fecha:</Text>
              <Text style={styles.value}>{currentDate}</Text>
            </View>
            <View style={styles.infoRow}>
              <Text style={styles.label}>Estado:</Text>
              <Text style={[styles.value, styles.status]}>{order.status}</Text>
            </View>
          </View>

          {/* Productos */}
          <View style={styles.section}>
            <Text style={styles.sectionTitle}>🛍️ Productos</Text>
            {order.items.map((item, index) => {
              const itemTotal = typeof item.total === 'number' ? item.total : parseFloat(item.total) || 0;
              const itemQuantity = item.quantity || 1;
              const unitPrice = itemTotal / itemQuantity;
              
              return (
                <View key={index} style={styles.productRow}>
                  <View style={styles.productInfo}>
                    <Text style={styles.productName}>{item.product?.nombre || 'Producto'}</Text>
                    <Text style={styles.productQuantity}>Cant: {itemQuantity} × Q{unitPrice.toFixed(2)}</Text>
                  </View>
                  <Text style={styles.productTotal}>Q{itemTotal.toFixed(2)}</Text>
                </View>
              );
            })}
          </View>

          {/* Totales */}
          <View style={styles.section}>
            <Text style={styles.sectionTitle}>💰 Resumen de Costos</Text>
            <View style={styles.totalRow}>
              <Text style={styles.totalLabel}>Subtotal:</Text>
              <Text style={styles.totalValue}>Q{(typeof order.subtotal === 'number' ? order.subtotal : parseFloat(order.subtotal) || 0).toFixed(2)}</Text>
            </View>
            <View style={styles.totalRow}>
              <Text style={styles.totalLabel}>Impuestos:</Text>
              <Text style={styles.totalValue}>Q{(typeof order.taxes === 'number' ? order.taxes : parseFloat(order.taxes) || 0).toFixed(2)}</Text>
            </View>
            <View style={styles.totalRow}>
              <Text style={styles.totalLabel}>Envío:</Text>
              <Text style={styles.totalValue}>Q{(typeof order.shipping === 'number' ? order.shipping : parseFloat(order.shipping) || 0).toFixed(2)}</Text>
            </View>
            <View style={[styles.totalRow, styles.grandTotalRow]}>
              <Text style={styles.grandTotalLabel}>TOTAL:</Text>
              <Text style={styles.grandTotalValue}>Q{(typeof order.total === 'number' ? order.total : parseFloat(order.total) || 0).toFixed(2)}</Text>
            </View>
          </View>

          {/* Dirección de envío */}
          <View style={styles.section}>
            <Text style={styles.sectionTitle}>📍 Envío</Text>
            <Text style={styles.addressText}>{order.shippingAddress?.name}</Text>
            <Text style={styles.addressText}>{order.shippingAddress?.address}</Text>
            <Text style={styles.addressText}>{order.shippingAddress?.city}</Text>
            <Text style={styles.addressText}>Tel: {order.shippingAddress?.phone}</Text>
          </View>

          {/* Método de pago */}
          <View style={styles.section}>
            <Text style={styles.sectionTitle}>💳 Método de Pago</Text>
            <Text style={styles.paymentMethod}>{order.paymentMethod?.descripcion || 'N/A'}</Text>
          </View>

          {/* Notas adicionales */}
          <View style={styles.section}>
            <Text style={styles.sectionTitle}>📝 Info Adicional</Text>
            <Text style={styles.notes}>
              • Procesado en 24h. Entrega: 3-5 días.{'\n'}
              • Recibirás confirmación por email.
            </Text>
          </View>
        </ScrollView>

        {/* Botones de acción */}
        <View style={styles.footer}>
          {/* Botón secundario pequeño para descargar PDF */}
          <TouchableOpacity 
            style={styles.pdfButton} 
            onPress={handlePrint}
            disabled={isGeneratingPDF}
          >
            {isGeneratingPDF ? (
              <ActivityIndicator color="white" size="small" />
            ) : (
              <>
                <Text style={styles.pdfButtonIcon}>📥</Text>
                <Text style={styles.pdfButtonText}>PDF</Text>
              </>
            )}
          </TouchableOpacity>
          
          {/* Botón principal Entendido - compartir por WhatsApp */}
          <TouchableOpacity 
            style={styles.closeActionButton} 
            onPress={handleClose}
            disabled={isSharingWhatsApp}
          >
            {isSharingWhatsApp ? (
              <ActivityIndicator color="white" size="small" />
            ) : (
              <Text style={styles.closeActionButtonText}>✅ Entendido</Text>
            )}
          </TouchableOpacity>
        </View>
      </View>
    </Modal>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#f8f9fa',
  },
  header: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: 20,
    backgroundColor: '#e74c3c',
    paddingTop: 50,
  },
  title: {
    fontSize: 20,
    fontWeight: 'bold',
    color: 'white',
  },
  closeButton: {
    backgroundColor: 'rgba(255,255,255,0.2)',
    width: 32,
    height: 32,
    borderRadius: 16,
    justifyContent: 'center',
    alignItems: 'center',
  },
  closeButtonText: {
    color: 'white',
    fontSize: 18,
    fontWeight: 'bold',
  },
  content: {
    flex: 1,
    padding: 20,
  },
  section: {
    backgroundColor: 'white',
    borderRadius: 12,
    padding: 16,
    marginBottom: 16,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 4,
    elevation: 3,
  },
  sectionTitle: {
    fontSize: 16,
    fontWeight: 'bold',
    color: '#2c3e50',
    marginBottom: 12,
  },
  infoRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 8,
  },
  label: {
    fontSize: 14,
    color: '#7f8c8d',
    flex: 1,
  },
  value: {
    fontSize: 14,
    fontWeight: '600',
    color: '#2c3e50',
    flex: 1,
    textAlign: 'right',
  },
  status: {
    color: '#27ae60',
    textTransform: 'uppercase',
  },
  productRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    marginBottom: 12,
    paddingBottom: 12,
    borderBottomWidth: 1,
    borderBottomColor: '#ecf0f1',
  },
  productInfo: {
    flex: 1,
    marginRight: 10,
  },
  productName: {
    fontSize: 14,
    fontWeight: 'bold',
    color: '#2c3e50',
    marginBottom: 4,
  },
  productQuantity: {
    fontSize: 12,
    color: '#7f8c8d',
    marginBottom: 2,
  },
  productPrice: {
    fontSize: 12,
    color: '#7f8c8d',
  },
  productTotal: {
    fontSize: 14,
    fontWeight: 'bold',
    color: '#27ae60',
  },
  totalRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 8,
  },
  totalLabel: {
    fontSize: 14,
    color: '#7f8c8d',
  },
  totalValue: {
    fontSize: 14,
    fontWeight: '600',
    color: '#2c3e50',
  },
  grandTotalRow: {
    borderTopWidth: 2,
    borderTopColor: '#e74c3c',
    paddingTop: 12,
    marginTop: 8,
  },
  grandTotalLabel: {
    fontSize: 18,
    fontWeight: 'bold',
    color: '#e74c3c',
  },
  grandTotalValue: {
    fontSize: 20,
    fontWeight: 'bold',
    color: '#e74c3c',
  },
  addressText: {
    fontSize: 14,
    color: '#2c3e50',
    marginBottom: 4,
  },
  paymentMethod: {
    fontSize: 14,
    fontWeight: '600',
    color: '#2c3e50',
  },
  notes: {
    fontSize: 14,
    color: '#7f8c8d',
    lineHeight: 20,
  },
  footer: {
    flexDirection: 'row',
    padding: 20,
    gap: 12,
    alignItems: 'center',
  },
  pdfButton: {
    backgroundColor: '#3498db',
    paddingVertical: 15,
    paddingHorizontal: 20,
    borderRadius: 8,
    alignItems: 'center',
    justifyContent: 'center',
    minWidth: 80,
    flexDirection: 'row',
    gap: 5,
  },
  pdfButtonIcon: {
    fontSize: 18,
  },
  pdfButtonText: {
    color: 'white',
    fontSize: 14,
    fontWeight: 'bold',
  },
  printButton: {
    flex: 1,
    backgroundColor: '#3498db',
    paddingVertical: 15,
    borderRadius: 8,
    alignItems: 'center',
  },
  printButtonText: {
    color: 'white',
    fontSize: 16,
    fontWeight: 'bold',
  },
  closeActionButton: {
    flex: 1,
    backgroundColor: '#27ae60',
    paddingVertical: 15,
    borderRadius: 8,
    alignItems: 'center',
  },
  closeActionButtonText: {
    color: 'white',
    fontSize: 16,
    fontWeight: 'bold',
  },
});

export default OrderSummaryModal;
